import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, EnvelopeSimple, MapPin, Phone } from "@phosphor-icons/react";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const industries = [
  "Metals & Steel",
  "Mining",
  "Construction & Infrastructure",
  "Manufacturing",
  "Realty & Facilities",
  "Education",
  "Agriculture",
  "Other",
];

const interests = [
  "Book a Consultation",
  "Request a Demo",
  "Partnership Inquiry",
  "General Inquiry",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    industry: "",
    interest: "Book a Consultation",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (!EMAIL_PATTERN.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (form.message.trim().length < 5) {
      toast.error("Please review the form and try again.");
      return;
    }
    setSubmitting(true);
    try {
      toast.success("Thanks — we'll be in touch shortly.");
      setSuccess(true);
      setForm({
        name: "",
        email: "",
        company: "",
        industry: "",
        interest: "Book a Consultation",
        message: "",
      });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* HERO */}
      <section className="border-b border-zinc-200" data-testid="contact-hero">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="text-overline mb-6">Contact</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
            Let&apos;s build the<br />intelligent enterprise.
          </h1>
          <p className="mt-8 text-xl leading-relaxed text-zinc-600 max-w-3xl">
            Talk to our industrial intelligence team — share your operation, your goals, and we&apos;ll
            respond with a tailored consultation or demo.
          </p>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-testid="contact-form-section">
        <div className="container-x mx-auto py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Left — Info */}
            <div className="lg:col-span-4">
              <div className="border border-zinc-200 bg-white p-8">
                <div className="text-overline text-zinc-500 mb-6">Get in touch</div>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <EnvelopeSimple size={22} weight="light" className="text-zinc-950 mt-0.5" />
                    <div>
                      <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">Email</div>
                      <a
                        href="mailto:info@bizlem.com"
                        data-testid="contact-email-link"
                        className="text-lg font-semibold text-zinc-950 hover:text-[#0A33D8] transition"
                      >
                        info@bizlem.com
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <Phone size={22} weight="light" className="text-zinc-950 mt-0.5" />
                    <div>
                      <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">Business hours</div>
                      <div className="text-lg font-semibold text-zinc-950">24×7 Intelligent Operations</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <MapPin size={22} weight="light" className="text-zinc-950 mt-0.5" />
                    <div>
                      <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">Focus</div>
                      <div className="text-lg font-semibold text-zinc-950">Global · Enterprise · Heavy Industry</div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="mt-6 border border-zinc-200 bg-white p-8">
                <div className="text-overline text-zinc-500 mb-4">Prefer a quick chat?</div>
                <p className="text-zinc-600 mb-6">
                  Send us a note using the form — we&apos;ll match you with the right industry expert
                  within one business day.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["30% Efficiency", "50+ AI Use Cases", "20+ Industries"].map((k) => (
                    <span key={k} className="text-xs font-mono uppercase tracking-widest border border-zinc-300 px-3 py-2">
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div className="lg:col-span-8">
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="border border-zinc-200 bg-white p-8 lg:p-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Full name *" name="name" value={form.name} onChange={onChange} testId="contact-input-name" required />
                  <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} testId="contact-input-email" required />
                  <Field label="Company" name="company" value={form.company} onChange={onChange} testId="contact-input-company" />
                  <SelectField
                    label="Industry"
                    name="industry"
                    value={form.industry}
                    onChange={onChange}
                    testId="contact-select-industry"
                    options={[{ value: "", label: "Select industry" }, ...industries.map((i) => ({ value: i, label: i }))]}
                  />
                  <SelectField
                    label="Interested in"
                    name="interest"
                    value={form.interest}
                    onChange={onChange}
                    testId="contact-select-interest"
                    className="sm:col-span-2"
                    options={interests.map((i) => ({ value: i, label: i }))}
                  />
                  <div className="sm:col-span-2">
                    <label className="text-overline text-zinc-500 block mb-2">Tell us about your operation *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={5}
                      required
                      data-testid="contact-input-message"
                      className="w-full border border-zinc-300 bg-white px-4 py-3 text-base text-zinc-950 focus:outline-none focus:border-zinc-950 transition"
                      placeholder="Briefly describe your industry, size, and the outcomes you're looking to unlock."
                    />
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                  <p className="text-xs text-zinc-500 max-w-md">
                    By submitting, you agree to be contacted by the Bizlem team regarding your inquiry.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-4 text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-md"
                  >
                    {submitting ? "Sending..." : "Send Inquiry"}
                    <ArrowUpRight size={16} weight="bold" />
                  </button>
                </div>

                {success && (
                  <div
                    data-testid="contact-success-banner"
                    className="mt-6 border border-zinc-950 bg-zinc-50 p-4 text-sm text-zinc-950"
                  >
                    Thank you — your inquiry has been received. A Bizlem expert will reach out shortly.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", testId, required = false }) {
  return (
    <div>
      <label className="text-overline text-zinc-500 block mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full border border-zinc-300 bg-white px-4 py-3 text-base text-zinc-950 focus:outline-none focus:border-zinc-950 transition"
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, testId, className = "" }) {
  return (
    <div className={className}>
      <label className="text-overline text-zinc-500 block mb-2">{label}</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full border border-zinc-300 bg-white px-4 py-3 text-base text-zinc-950 focus:outline-none focus:border-zinc-950 transition"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
